import { ChatActivityEvent } from "@/api/chat";

const SILENCE_BEFORE_CHIME_MS = 10 * 60 * 1000;
const LAST_CHIME_STORAGE_KEY = "fireflies-chat-last-chime-message";

type AudioWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

let audioContext: AudioContext | null = null;

function getAudioContext() {
  if (audioContext) return audioContext;
  const AudioContextClass =
    window.AudioContext || (window as AudioWindow).webkitAudioContext;
  if (!AudioContextClass) return null;
  audioContext = new AudioContextClass();
  return audioContext;
}

export async function unlockChatSound() {
  const context = getAudioContext();
  if (context && context.state !== "running" && context.state !== "closed") {
    try {
      await context.resume();
    } catch {
      // Некоторые мобильные браузеры разрешат звук только при следующем жесте.
    }
  }
}

export function findWakeChimeEvent(
  events: ChatActivityEvent[],
  previousMessageCreatedAt: string | null
) {
  let previousTimestamp = previousMessageCreatedAt
    ? new Date(previousMessageCreatedAt).getTime()
    : null;

  for (const event of events) {
    const timestamp = new Date(event.created_at).getTime();
    const followsSilence =
      previousTimestamp !== null &&
      Number.isFinite(timestamp) &&
      timestamp - previousTimestamp >= SILENCE_BEFORE_CHIME_MS;
    previousTimestamp = timestamp;

    if (followsSilence && !event.is_own) return event;
  }

  return null;
}

async function playChimeTone() {
  const context = getAudioContext();
  if (!context) return false;

  try {
    if (context.state !== "running" && context.state !== "closed") {
      await context.resume();
    }
    if (context.state !== "running") return false;

    // Give a suspended or idle audio device a short pre-roll. Starting at
    // currentTime clips the attack on some mobile browsers and after a long
    // inactive tab, making the first chime sound quiet or start halfway in.
    const startAt = context.currentTime + 0.1;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(0.085, startAt + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.7);
    gain.connect(context.destination);

    [880, 1174.66].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, startAt);
      oscillator.connect(gain);
      oscillator.start(startAt + index * 0.1);
      oscillator.stop(startAt + 0.58 + index * 0.1);
    });
    return true;
  } catch {
    // Звук является необязательным улучшением и не должен ломать чат.
    return false;
  }
}

export async function playChatChime(messageId: number) {
  const lastPlayedId = Number(localStorage.getItem(LAST_CHIME_STORAGE_KEY));
  if (lastPlayedId === messageId) return;

  if (await playChimeTone()) {
    localStorage.setItem(LAST_CHIME_STORAGE_KEY, String(messageId));
  }
}

export async function playChatChimePreview() {
  await playChimeTone();
}
